import React, { useState, useEffect } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BookOpen, FileText, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const TranslatorCurrentWork = () => {
  const [assignedBooks, setAssignedBooks] = useState([]);
  const [currentBook, setCurrentBook] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [booksRes, currentRes] = await Promise.all([
        axios.get(`${API}/translator/assigned-books`),
        axios.get(`${API}/translator/current-work`)
      ]);
      setAssignedBooks(booksRes.data);
      setCurrentBook(currentRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Kon nie data laai nie');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusBadge = (status) => {
    const statusMap = {
      'Besig met Vertaling': { color: 'bg-blue-100 text-blue-700', icon: Clock },
      'Wag vir Proeflees': { color: 'bg-amber-100 text-amber-700', icon: Clock },
      'Proeflees Voltooi': { color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
      'Publiseer': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'Gepubliseer': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
    };
    const config = statusMap[status] || { color: 'bg-gray-100 text-gray-700', icon: FileText };
    const Icon = config.icon;
    return (
      <Badge className={config.color}>
        <Icon className="h-3 w-3 mr-1" />
        {status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="translator-current-work">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Huidige Werk
        </h2>
        <p className="text-gray-600">Jou toegekende boeke en huidige vertalings</p>
      </div>

      {/* Current Active Book */}
      {currentBook && Object.keys(currentBook).length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-800">
              <BookOpen className="h-5 w-5" />
              Huidige Vertaling
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">{currentBook.boek_naam}</h3>
                <p className="text-gray-600">Skrywer: {currentBook.skrywer_naam}</p>
                <p className="text-gray-500 text-sm mt-1">
                  {currentBook.page_count} bladsye • 
                  Begin: {new Date(currentBook.translation_started_date || currentBook.created_date).toLocaleDateString('af-ZA')}
                </p>
              </div>
              {getStatusBadge(currentBook.status)}
            </div>
          </CardContent>
        </Card>
      )}

      {/* All Assigned Books */}
      <Card>
        <CardHeader>
          <CardTitle>Alle Toegekende Boeke</CardTitle>
        </CardHeader>
        <CardContent>
          {assignedBooks.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Geen boeke toegeken nie</h3>
              <p className="text-gray-500">Vra 'n boek aan om te begin vertaal</p>
            </div>
          ) : (
            <div className="space-y-4">
              {assignedBooks.map((book) => (
                <div 
                  key={book.id} 
                  className="p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{book.boek_naam}</h4>
                      <p className="text-gray-600 text-sm">Skrywer: {book.skrywer_naam}</p>
                      <p className="text-gray-500 text-sm">
                        {book.page_count} bladsye • 
                        {new Date(book.created_date).toLocaleDateString('af-ZA')}
                      </p>
                    </div>
                    {getStatusBadge(book.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <h3 className="font-semibold text-blue-800 mb-2">Vertaling Proses</h3>
          <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
            <li>Vra 'n boek aan en wag vir goedkeuring</li>
            <li>Ontvang die boek en begin vertaal</li>
            <li>Dien die vertaling in wanneer klaar</li>
            <li>'n Proefleser sal die vertaling nagaan</li>
            <li>Ontvang betaling (minus fout-aftrekkings)</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
};

export default TranslatorCurrentWork;
